﻿import {
  DropOptions,
  getBackendOptions,
  MultiBackend,
  NodeModel,
  Tree,
} from "@minoru/react-dnd-treeview";
import { useEffect, useState } from "react";
import { DndProvider } from "react-dnd";
import { OrderableTreeItem } from "./components";
import { IOrderableTreeItem } from "./interfaces";
import * as S from "./styled";

const ROOT_ID = 0;

interface IProps<T> {
  data: IOrderableTreeItem<T>[];
  handleReorder: (
    treeItem: IOrderableTreeItem<T>,
    orederedDataSubset: T[]
  ) => void;
}

const OrderableTree = <T,>({ data, handleReorder }: IProps<T>) => {
  const [treeData, setTreeData] = useState<NodeModel<T>[]>([]);

  useEffect(() => {
    setTreeData(
      data.map(({ id, parentId, text, droppable, data: treeItemData }) => ({
        id,
        droppable: droppable || true,
        parent: parentId || ROOT_ID,
        text,
        data: treeItemData,
      }))
    );
  }, [data]);

  const handleDrop = (
    newTreeData: NodeModel<T>[],
    { dragSource, dropTarget }: DropOptions<T>
  ): void => {
    setTreeData(newTreeData);

    if (!dragSource) return;

    const { id, text, data } = dragSource;

    const treeItem: IOrderableTreeItem<T> = {
      data: data,
      id,
      parentId: dropTarget?.id || ROOT_ID,
      text,
    };

    const orderedDataSubset: T[] = newTreeData
      .filter(({ parent }) => parent === treeItem.parentId && !!data)
      .map(({ data }) => data) as T[];

    handleReorder(treeItem, orderedDataSubset);
  };

  if (!treeData.length) return null;

  return (
    <DndProvider backend={MultiBackend} options={getBackendOptions()}>
      <Tree<T>
        tree={treeData}
        rootId={ROOT_ID}
        sort={false}
        initialOpen
        insertDroppableFirst={false}
        dropTargetOffset={10}
        canDrop={(tree, { dragSource, dropTargetId }): boolean | void => {
          if (dragSource?.parent === dropTargetId) {
            return true;
          }
        }}
        render={(node, options) => (
          <OrderableTreeItem node={node} {...options} />
        )}
        dragPreviewRender={({ item }) => (
          <S.DragPreview>{item.text}</S.DragPreview>
        )}
        placeholderRender={() => <S.Placeholder />}
        onDrop={handleDrop}
        classes={{
          root: "list-none pl-0 inline-block",
          listItem: "list-none pl-0",
          placeholder: "list-none",
          dropTarget: "viper-drop-target",
        }}
      />
    </DndProvider>
  );
};

export default OrderableTree;
